import React, { useState, useEffect } from 'react';
import { AlertCircle, Loader2, ChevronLeft, User } from 'lucide-react';
import { metricsAPI, handleMetricsAPIError, metricsUtils } from './api';

const MemberAnalysisTab = ({ groupData, isDark = false, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState(null);

  useEffect(() => {
    if (groupData && groupData.group_id) {
      fetchMemberData();
    }
  }, [groupData]);

  const fetchMemberData = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { 
        period: groupData.selectedPeriod || 'month' 
      };
      
      // Add custom date range if provided
      if (groupData.selectedPeriod === 'custom' && groupData.customDateRange) {
        const { startDate, endDate } = groupData.customDateRange;
        if (startDate && endDate) {
          params.start_date = metricsUtils.formatDate(startDate);
          params.end_date = metricsUtils.formatDate(endDate);
        }
      }
      
      const response = await metricsAPI.getGroupMemberAnalysis(groupData.group_id, params);

      if (response.success) {
        setMemberData(response.data);
      } else {
        setError('Failed to load member data');
      }
    } catch (err) {
      setError(handleMetricsAPIError(err));
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    const periodMap = {
      'today': 'today',
      'week': 'this week',
      'month': 'this month',
      'quarter': 'this quarter',
      'year': 'this year',
      'custom': 'custom period'
    };
    return periodMap[groupData?.selectedPeriod] || 'this month';
  };

  const getColorForValue = (value) => {
    if (value > 5000) return 'text-green-600';
    if (value > 2000) return 'text-blue-600';
    if (value > 500) return 'text-orange-600';
    return isDark ? 'text-gray-300' : 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Loading member analysis...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
        <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
        <button 
          onClick={fetchMemberData}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!memberData || !memberData.members || memberData.members.length === 0) {
    return (
      <div className={`rounded-lg p-6 text-center ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <AlertCircle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
        <p className={`text-lg ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          No member activity found for this group in {getPeriodLabel()}
        </p>
      </div>
    );
  }

  const { members, group_analysis } = memberData;

  // Calculate totals
  const totals = members.reduce((acc, member) => {
    acc.total_repaid += member.total_repaid || 0;
    acc.loans_repaid += member.loans_repaid || 0;
    acc.interest_on_loans += member.interest_on_loans || 0;
    acc.advance_repaid += member.advance_repaid || 0;
    acc.interest_on_advance += member.interest_on_advance || 0;
    acc.savings += member.savings || 0;
    acc.others += member.others || 0;
    acc.pnl += member.pnl || 0;
    acc.loan_arrears += member.loan_arrears || 0;
    acc.advance_arrears += member.advance_arrears || 0;
    acc.total_arrears += member.total_arrears || 0;
    return acc;
  }, {
    total_repaid: 0,
    loans_repaid: 0,
    interest_on_loans: 0,
    advance_repaid: 0,
    interest_on_advance: 0,
    savings: 0,
    others: 0,
    pnl: 0,
    loan_arrears: 0,
    advance_arrears: 0,
    total_arrears: 0
  });

  const isPnLPositive = totals.pnl >= 0;

  return (
    <div>
      {/* Back Button and Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className={`mb-4 flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            isDark 
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Groups
        </button>

        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {groupData.group_name}
              </h2>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Member Performance Analysis - {groupData.meeting_date}
              </p>
              {groupData.period_name && (
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {groupData.period_name}
                </p>
              )}
              <p className={`text-xs mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                Showing data for {getPeriodLabel()}
                {groupData.selectedPeriod === 'custom' && groupData.customDateRange && groupData.customDateRange.startDate && groupData.customDateRange.endDate && (
                  <span className="ml-1">
                    ({groupData.customDateRange.startDate} to {groupData.customDateRange.endDate})
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Members Table */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                <th className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Member
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Total Repaid
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Savings
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Loans Repaid
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Int on Loans
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Adv. Repaid
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Int on Adv.
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Others
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  P&L
                </th>
                <th className={`px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Arrears
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {members.map((member, index) => (
                <tr key={index} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {member.member_name}
                    </div>
                    <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      {member.member_number}
                    </div>
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.total_repaid)}`}>
                    KSh {member.total_repaid.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.savings)}`}>
                    KSh {member.savings.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.loans_repaid)}`}>
                    KSh {member.loans_repaid.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.interest_on_loans)}`}>
                    KSh {member.interest_on_loans.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.advance_repaid)}`}>
                    KSh {member.advance_repaid.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.interest_on_advance)}`}>
                    KSh {member.interest_on_advance.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-semibold ${getColorForValue(member.others)}`}>
                    KSh {member.others.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-bold ${member.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    KSh {member.pnl.toLocaleString()}
                  </td>
                  <td className={`px-4 py-4 whitespace-nowrap text-right font-bold text-red-600`}>
                    KSh {member.total_arrears.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={`border-t-2 ${isDark ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
              <tr>
                <td className={`px-4 py-4 font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  TOTAL
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.total_repaid.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.savings.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.loans_repaid.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.interest_on_loans.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.advance_repaid.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.interest_on_advance.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  KSh {totals.others.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold ${isPnLPositive ? 'text-green-600' : 'text-red-600'}`}>
                  KSh {totals.pnl.toLocaleString()}
                </td>
                <td className={`px-4 py-4 text-right font-bold text-red-600`}>
                  KSh {totals.total_arrears.toLocaleString()}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Interest</div>
          <div className="text-2xl font-bold text-blue-600">
            KSh {(totals.interest_on_advance + totals.interest_on_loans).toLocaleString()}
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Savings</div>
          <div className="text-2xl font-bold text-green-600">
            KSh {totals.savings.toLocaleString()}
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total P&L</div>
          <div className={`text-2xl font-bold ${isPnLPositive ? 'text-green-600' : 'text-red-600'}`}>
            KSh {totals.pnl.toLocaleString()}
          </div>
        </div>
        
        <div className={`p-4 rounded-lg ${isDark ? 'bg-[#2b323c] border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Arrears</div>
          <div className="text-2xl font-bold text-red-600">
            KSh {totals.total_arrears.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Group Analysis Summary */}
      <div className={`mt-6 rounded-lg shadow-sm border p-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Group Analysis Summary
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Net Cash Remitted
            </div>
            <div className="text-2xl font-bold text-blue-600">
              KSh {group_analysis.net_cash_remitted.toLocaleString()}
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Loan Arrears
            </div>
            <div className="text-2xl font-bold text-orange-600">
              KSh {group_analysis.loan_arrears.toLocaleString()}
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Advance Arrears
            </div>
            <div className="text-2xl font-bold text-orange-600">
              KSh {group_analysis.advance_arrears.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-red-400' : 'text-red-700'}`}>
              Total Arrears
            </div>
            <div className="text-2xl font-bold text-red-600">
              KSh {group_analysis.total_arrears.toLocaleString()}
            </div>
          </div>
          
          <div className={`p-4 rounded-lg ${isDark ? 'bg-purple-900/20 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
            <div className={`text-sm mb-1 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>
              Expected Profit from Arrears
            </div>
            <div className="text-2xl font-bold text-purple-600">
              KSh {group_analysis.expected_profit_from_arrears.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberAnalysisTab;